// De qué idioma es el jugador. Un solo lugar, porque hasta T1 era el literal
// `"es"` escrito a mano en tres pantallas.
//
// T1 de `odd/tasks/el-idioma-del-jugador.md`.
//
// LO QUE HABÍA
// ------------
// `language:"es"` tipeado en `App.jsx:3989`, `Web.jsx:3282` y
// `SportsbookC360.jsx:55`, con una prueba que lo afirmaba. No era una
// configuración mal puesta: el idioma no existía como dato en ninguna parte
// del sistema. El backend, en cambio, ya sabe qué hacer con él: toma
// `body["language"]` y lo lleva hasta `idioma_de()` de Atomic y de Content360
// (`casino_api.py:32654`, `atomic.py:293`, `content360.py:544`). La cañería
// estaba puesta y le entraba una constante.
//
// EL NAVEGADOR ES SEMILLA, NO SENTENCIA
// --------------------------------------
// Decisión del dueño (2026-10-28): el idioma arranca del navegador y el
// jugador lo cambia. Por eso `idiomaDelJugador` mira primero su elección
// —`user.idioma`, que llega en T2— y sólo después el navegador. El orden
// importa: al revés, un jugador que eligió inglés volvería a portugués cada
// vez que abre la app desde otro teléfono.
//
// Se descartó resolverlo por IP, que era el camino barato porque el molde de
// `_moneda_por_ip` ya existe. La IP da PAÍS, no idioma: un brasileño con IP
// argentina abriría todo en español y sin forma de cambiarlo. Para la moneda
// el país alcanza; para el idioma, no.
//
// TRES IDIOMAS, Y EL CUARTO NO ES GRATIS
// ---------------------------------------
// `IDIOMAS` arranca en es/en/pt porque son los que Content360 soporta de
// verdad: su tabla es `{es, en, pt}` (`bot/content360.py:509`) y lo que no
// está ahí abre el juego en `es_ES` SIN AVISAR. Mandarle `tr` no da un error
// que se pueda ver; da un juego en español y un jugador turco mirándolo. Por
// eso la lista es corta a propósito y sumarle idiomas pide antes la lista de
// locales del proveedor.
//
// LO QUE NO ESTÁ EN LA LISTA NO SE DEJA PASAR
// --------------------------------------------
// Mismo criterio que `moneda.js` con una moneda desconocida: no se disfraza.
// Un idioma que no soportamos cae a `es` acá, donde se ve en una línea, en vez
// de viajar hasta el proveedor y caer a español allá, a tres archivos de
// distancia. Y se parte por el separador en vez de recortar a dos letras:
// `"est".slice(0,2)` es `"es"`, o sea que el estonio se convertiría en español
// callado.

// Los idiomas que el producto sabe hablar de punta a punta.
export const IDIOMAS = ["es", "en", "pt"];

// A qué se cae cuando no hay nada que sirva. Es el idioma de la mayoría de los
// jugadores, y el que los dos proveedores usan de respaldo.
export const PREDETERMINADO = "es";

// Cómo se llama cada idioma EN ESE IDIOMA. No es un detalle de estilo: quien
// busca inglés en una lista está buscando la palabra "English", no "Inglés",
// porque justamente no lee la que está puesta. Un selector traducido al idioma
// que el jugador no entiende es un selector inútil.
//
// Portugués lleva la región entre paréntesis porque el nuestro es el de Brasil
// (Content360 lo entrega como `pt_BR`) y en Portugal se escribe distinto.
export const NOMBRES = {
  es: "Español",
  en: "English",
  pt: "Português (Brasil)",
};

/** La etiqueta de idioma de un BCP 47, si la soportamos; `null` si no.
 *
 * `"pt-BR"` → `"pt"`, `"es_AR"` → `"es"`, `"est"` → `null` (estonio, no
 * español), `"fr"` → `null`. */
export function idiomaSoportado(texto) {
  if (typeof texto !== "string") return null;
  const etiqueta = texto.trim().toLowerCase().split(/[-_]/)[0];
  return IDIOMAS.includes(etiqueta) ? etiqueta : null;
}

/** El primer idioma de los que pidió el navegador que sepamos hablar.
 *
 * Se recorre la lista entera y no sólo la primera: si el jugador pidió
 * francés, inglés, español, tenemos que darle inglés y no español. Esa lista
 * es su orden de preferencia y descartarla sería elegir por él. */
export function idiomaDelNavegador(
  nav = typeof navigator === "undefined" ? null : navigator
) {
  const pedidos = nav && nav.languages && nav.languages.length
    ? nav.languages
    : (nav && nav.language ? [nav.language] : []);
  for (const pedido of pedidos) {
    const idioma = idiomaSoportado(pedido);
    if (idioma) return idioma;
  }
  return null;
}

/** El idioma con el que hay que abrirle el juego a este jugador.
 *
 * Su elección primero, el navegador después, español al final. Devuelve
 * siempre uno de `IDIOMAS`: quien llama no tiene que validar nada. */
export function idiomaDelJugador(user, nav) {
  return idiomaSoportado(user && user.idioma)
    || idiomaDelNavegador(nav)
    || PREDETERMINADO;
}
